import type { HighlightID } from "@store/highlights"
import "./fade.css"
import "canvas-roundrect-polyfill"
import { SVGRenderer } from "./svg-canvas"
import "./pop-words.css"

export interface RendererRect {
    x: number
    y: number
    width: number
    height: number
}

/**
 * Responsible for selected words, like in search results.
 */
export class PopCanvas extends SVGRenderer {
    static Class = "highlighter--popwords"
    constructor(parent : HTMLElement) {
        super(parent)
        this.show()
    }
    drawSelectedWords(rects: RendererRect[]) {
        this.clear()
        this.drawRectangles(rects, { class: PopCanvas.Class })
        this.draw()
        this.show()
    }
}

export class SelectionCanvas extends SVGRenderer {
    static Color = "#8dc6d757"
    constructor(parent: HTMLElement) {
        super(parent)
        this.show()
    }

    clearSelection() {
        this.clear()
        this.draw()
    }

    drawSelection(rects: RendererRect[]) {
        this.clear()
        this.drawRectangles(rects, { fill: SelectionCanvas.Color })
        this.draw()
        this.show()
    }
}

export class HighlightCanvas {
    #highlightRectMap: Map<HighlightID, HighlightRectsInstance> = new Map()
    #renderer: SVGRenderer
    constructor(parent: HTMLElement) {
        this.#renderer = new SVGRenderer(parent)
    }

    /**
     * Set or update
     * @param instance
     * @param redraw
     */
    setHighlight(instance: HighlightRectsInstance, redraw = true) {
        this.#highlightRectMap.set(instance.id, instance)
        if (redraw) this.redrawHighlights()
    }

    removeAllHighlights(redraw = false) {
        this.#highlightRectMap = new Map()
        if (redraw) this.redrawHighlights()
    }

    hide() {
        this.#renderer.hide()
    }

    resizeCanvas() {
        this.#renderer.resizeCanvas()
    }

    testHitboxPoint(x: number, y: number): HighlightID[] {
        return [...this.#highlightRectMap.values()]
            .filter((instance) => instance.testHit(x, y))
            .map((instance) => instance.id)
    }

    /**
     *
     * @param id
     * @param redraw
     * @returns true if deleted, false if not
     */
    removeHighlight(id: HighlightID, redraw = true): boolean {
        const deleted = this.#highlightRectMap.delete(id)
        if (redraw) this.redrawHighlights()
        return deleted
    }

    static SelectedOutlineStroke = "#93c5fd" // <--- tailwind blue-300, same as outline //"orange"

    redrawHighlights({
        focussed = null,
        emphasize = null,
    }: { focussed?: HighlightID | null; emphasize?: HighlightID | "faded" | null } = {}) {
        this.#renderer.clear()
        this.#highlightRectMap.forEach((highlightRect) => {
            const { rects, className, id, note } = highlightRect
            const isFocussed = id === focussed
            this.#renderer.drawRectangles(rects, {
                class: className,
                ...(emphasize !== null && emphasize !== id ? { opacity: "0.2" } : {}),
                ...(isFocussed === true
                    ? { stroke: HighlightCanvas.SelectedOutlineStroke, "stroke-width": "2" }
                    : {}),
            })
            if (note === true) {
                this.#renderer.drawLine(highlightRect.underline, {
                    stroke: "#00F4",
                    "stroke-width": 3 / 2 + "",
                    "stroke-linecap": "round",
                })
            }
        })

        this.#renderer.draw()
        // Show open animation if not showing
        if (!this.#renderer.showing) this.#renderer.show()

        // if (focussed === null) return
        // const rectsForFocussed = this.#highlightRectMap.get(focussed)
        // if (rectsForFocussed === undefined)
        //     throw new Error("Focussed highlight was not defined during redraw")
        // this.#renderer.stroke(
        //     rectsForFocussed.rects,
        //     HighlightCanvas.SelectedOutlineStroke
        // )
    }
}

/**
 * For making underline strokes if needed
 * @param rects
 * @returns
 */
function gluedRectsToUnderline(
    rects: RendererRect[],
    { compress = 0, distance = 0 } = {}
): RendererLine[] {
    return rects.map((rect) => {
        return {
            from: {
                x: rect.x + compress,
                y: rect.y + rect.height + distance,
            },
            to: {
                x: rect.x + rect.width - compress,
                y: rect.y + rect.height + distance,
            },
        }
    })
}

export interface RendererLine {
    from: RendererCoordinate
    to: RendererCoordinate
}

export interface RendererCoordinate {
    x: number
    y: number
}

export class HighlightRectsInstance {
    static HitBoxPadding = 10
    #hitboxes: RendererRect[] | null = null
    constructor(
        public readonly id: HighlightID,
        public readonly className: string,
        public readonly rects: RendererRect[],
        public readonly note: boolean
    ) {}

    get underline() {
        return gluedRectsToUnderline(this.rects, {
            compress: 5,
            distance: 2,
        })
    }

    get hitboxes() {
        if (this.#hitboxes !== null) return this.#hitboxes
        const hitboxes: RendererRect[] = this.rects.map((rect) => {
            return {
                y: rect.y - HighlightRectsInstance.HitBoxPadding,
                x: rect.x - HighlightRectsInstance.HitBoxPadding,
                height: rect.height + 2 * HighlightRectsInstance.HitBoxPadding,
                width: rect.width + 2 * HighlightRectsInstance.HitBoxPadding,
            }
        })
        this.#hitboxes = hitboxes
        return hitboxes
    }

    testHit(x: number, y: number): boolean {
        for (const hitbox of this.hitboxes) {
            const inX = hitbox.x < x && x < hitbox.x + hitbox.width
            const inY = hitbox.y < y && y < hitbox.y + hitbox.height
            if (inX && inY) return true
        }
        return false
    }
}
